import React from "react";
import { Languages, MapPin } from "lucide-react";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "@/components/sponsors/ProgramBadge";
import AvailabilityPill, { PresencePill } from "@/components/sponsors/AvailabilityPill";

/**
 * Identity header card on a sponsor profile: avatar, name, pathway badge,
 * recovery/sponsoring years, location, language and availability.
 *
 * Extracted verbatim from src/pages/SponsorProfile.jsx during the code-quality
 * pass: markup, copy, data-testids and behaviour are unchanged.
 */
export default function SponsorProfileHeader({ sponsor, user: u, loc }) {
  return (
    <div className="sm-card p-6 bg-white">
      <div className="flex flex-col sm:flex-row items-start gap-5">
        <MemberAvatar user={u} size={88} />
        <div className="flex-1 min-w-0">
          <h1
            data-testid="sponsor-profile-name"
            className="font-display text-3xl font-bold text-[#1F2A2E]"
          >
            {u.display_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ProgramBadge program={sponsor.recovery_program} size="lg" showFull />
            <PresencePill presence={sponsor.presence} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[#4B5A60]">
            {sponsor.years_in_recovery ? (
              <span>
                <span className="font-semibold text-[#1F2A2E]">{sponsor.years_in_recovery}</span>{" "}
                years in recovery
              </span>
            ) : null}
            {sponsor.years_sponsoring ? (
              <span>
                <span className="font-semibold text-[#1F2A2E]">{sponsor.years_sponsoring}</span>{" "}
                years sponsoring
              </span>
            ) : null}
            {loc ? (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> {loc}
              </span>
            ) : null}
            {sponsor.language ? (
              <span className="inline-flex items-center gap-1">
                <Languages className="w-3.5 h-3.5" /> {sponsor.language}
              </span>
            ) : null}
          </div>
          <div className="mt-4">
            <AvailabilityPill status={sponsor.availability_status} size="lg" />
          </div>
        </div>
      </div>
    </div>
  );
}
